/**
 * Check-In Notification Service
 * Manages notifications for VAs before check-in time
 * Sends notifications 3 minutes before check-in is due
 */

import { supabase } from '../utils/supabase';

// Constants
const NOTIFICATION_ADVANCE_MINUTES = 3;
const CHECK_INTERVAL_SECONDS = 30; // Check every 30 seconds for due notifications

/**
 * Create a check-in reminder notification
 * @param {string} vaId - VA's user ID
 * @param {Date} checkInDueTime - Time when check-in is due
 * @param {string} shiftId - Shift ID for context
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const createCheckInReminder = async (vaId, checkInDueTime, shiftId) => {
  try {
    if (!vaId || !checkInDueTime) {
      throw new Error('Missing VA ID or check-in due time');
    }

    const { data, error } = await supabase
      .from('notifications')
      .insert({
        user_id: vaId,
        title: 'Check-In Reminder',
        message: 'Time to check in! You have 3 minutes before your check-in is due.',
        type: 'check_in_reminder',
        data: {
          shift_id: shiftId,
          due_time: checkInDueTime.toISOString(),
          reminder_type: 'pre_check_in'
        }
      })
      .select()
      .single();

    if (error) throw error;

    console.log('Check-in reminder notification created:', { vaId, shiftId });
    return { data, error: null };
  } catch (error) {
    console.error('Error creating check-in reminder:', error);
    return { data: null, error };
  }
};

/**
 * Schedule check-in reminders for all active VAs
 * This should be called periodically (e.g., every 30 seconds) to check for due reminders
 * @returns {Promise<{notificationsCreated: number, error: Error|null}>}
 */
export const scheduleCheckInReminders = async () => {
  try {
    const now = new Date();
    const reminderTime = new Date(now.getTime() + NOTIFICATION_ADVANCE_MINUTES * 60000);

    // Find all active shifts that need reminders
    // Look for shifts where next_checkin_due is within the next 3 minutes
    const { data: shiftsNeedingReminders, error: shiftsError } = await supabase
      .from('va_shifts')
      .select('id, va_id, next_checkin_due, contract_id')
      .eq('status', 'active')
      .gte('next_checkin_due', now.toISOString())
      .lte('next_checkin_due', reminderTime.toISOString());

    if (shiftsError) throw shiftsError;

    if (!shiftsNeedingReminders || shiftsNeedingReminders.length === 0) {
      return { notificationsCreated: 0, error: null };
    }

    console.log(`Found ${shiftsNeedingReminders.length} shifts needing reminders`);

    // Check which reminders already exist to avoid duplicates
    const shiftIds = shiftsNeedingReminders.map(s => s.id);
    
    const { data: existingNotifications, error: notifError } = await supabase
      .from('notifications')
      .select('data')
      .in('data->>shift_id', shiftIds)
      .eq('type', 'check_in_reminder')
      .gte('created_at', new Date(now.getTime() - 60000).toISOString()); // Within last minute

    if (notifError) {
      console.warn('Could not check for existing notifications:', notifError);
    }

    const existingShiftIds = new Set(
      (existingNotifications || []).map(n => n.data?.shift_id)
    );

    // Create reminders for shifts that don't have one yet
    let notificationsCreated = 0;
    const shiftsToNotify = shiftsNeedingReminders.filter(
      shift => !existingShiftIds.has(shift.id)
    );

    for (const shift of shiftsToNotify) {
      const { error: reminderError } = await createCheckInReminder(
        shift.va_id,
        new Date(shift.next_checkin_due),
        shift.id
      );

      if (!reminderError) {
        notificationsCreated++;
      } else {
        console.error('Failed to create reminder for shift:', shift.id, reminderError);
      }
    }

    return { notificationsCreated, error: null };
  } catch (error) {
    console.error('Error scheduling check-in reminders:', error);
    return { notificationsCreated: 0, error };
  }
};

/**
 * Start polling for check-in reminders
 * Should be called when a VA starts a shift or when the app initializes
 * Returns an interval ID that can be used to stop polling
 * @returns {number} Interval ID
 */
export const startCheckInReminderPolling = () => {
  console.log('Starting check-in reminder polling');
  
  const intervalId = setInterval(async () => {
    const { notificationsCreated, error } = await scheduleCheckInReminders();
    
    if (error) {
      console.error('Error in check-in reminder polling:', error);
    } else if (notificationsCreated > 0) {
      console.log(`Created ${notificationsCreated} check-in reminders`);
    }
  }, CHECK_INTERVAL_SECONDS * 1000);

  return intervalId;
};

/**
 * Stop polling for check-in reminders
 * @param {number} intervalId - Interval ID from startCheckInReminderPolling
 */
export const stopCheckInReminderPolling = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
    console.log('Stopped check-in reminder polling');
  }
};

/**
 * Mark a check-in reminder as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<{success: boolean, error: Error|null}>}
 */
export const markCheckInReminderAsRead = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { success: false, error };
  }
};

/**
 * Get all unread check-in reminders for a VA
 * @param {string} vaId - VA's user ID
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const getUnreadCheckInReminders = async (vaId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', vaId)
      .eq('type', 'check_in_reminder')
      .eq('is_read', false)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching unread reminders:', error);
    return { data: null, error };
  }
};

export default {
  createCheckInReminder,
  scheduleCheckInReminders,
  startCheckInReminderPolling,
  stopCheckInReminderPolling,
  markCheckInReminderAsRead,
  getUnreadCheckInReminders,
  NOTIFICATION_ADVANCE_MINUTES,
  CHECK_INTERVAL_SECONDS
};
